/* Los códigos de barras de un artículo (`stock_codigos`, ticket 466).
 *
 * La tabla la escribe FlotaBot cuando alguien escanea un código que no conoce.
 * Desde el 23/10 también el hub, al editar el artículo. Los permisos ya están
 * desde la migración que la creó (20261021140000): leen `ver.panol` o
 * `ver.taller`, escribe `cargar.panol` —la misma llave que edita `stock`—, así
 * que no hizo falta tocar la base.
 *
 * No va por `supabaseRealtime.createRow`: esa función sella `usuario` y la
 * tabla no tiene esa columna (tiene `creado_por` y `creado_nombre`, que son
 * los que llena FlotaBot). Mandarle `usuario` haría rebotar la fila entera.
 */
import { supabase } from "../supabase/SupabaseClient";
import { actual } from "./quien";

const TABLA = "stock_codigos";

/* `{codigos, error}`. «No pude leer» no es «no tiene»: sin esta distinción
 * un artículo con tres códigos se vería sin ninguno el día que falle la red,
 * y la persona los cargaría de nuevo. */
export async function codigosDelArticulo(articulo) {
  const { data, error } = await supabase
    .from(TABLA)
    .select("codigo, formato, unidades, origen, creado_nombre, creado_en")
    .eq("articulo", String(articulo))
    .order("creado_en", { ascending: true });
  if (error) {
    console.warn("[stockCodigos] no pude leer los códigos:", error.message);
    return { codigos: [], error: error.message || "no se pudo leer" };
  }
  return { codigos: data || [], error: "" };
}

/* De quién es un código que ya existe, para decirlo en el cartel. */
async function deQuienEs(codigo) {
  const { data } = await supabase
    .from(TABLA)
    .select("articulo")
    .eq("codigo", codigo)
    .maybeSingle();
  return data?.articulo || "";
}

const esDuplicado = (error) =>
  String(error?.code || "") === "23505" ||
  /duplicate key|already exists/i.test(String(error?.message || ""));

/* Aplica `{quitar, agregar}` (ver `cambiosDeCodigos`). Devuelve la lista de
 * lo que NO se pudo, en castellano, para que el cartel lo diga: un código que
 * no entró y nadie se entera es un escaneo que mañana contesta «no lo
 * conozco». Primero se quita y después se agrega, así pasar un código de un
 * renglón a otro del mismo artículo no choca consigo mismo. */
export async function aplicarCambiosDeCodigos(articulo, { quitar = [], agregar = [] } = {}) {
  const problemas = [];
  for (const codigo of quitar) {
    const { error } = await supabase
      .from(TABLA)
      .delete()
      .eq("codigo", codigo)
      .eq("articulo", String(articulo));
    if (error) problemas.push(`No pude quitar ${codigo}: ${error.message}`);
  }
  const yo = actual();
  for (const c of agregar) {
    const { error } = await supabase.from(TABLA).insert({
      codigo: c.codigo,
      articulo: String(articulo),
      formato: c.formato === "gtin" ? "gtin" : "otro",
      origen: "hub",
      creado_por: yo?.mail || null,
      creado_nombre: yo?.nombre || null,
    });
    if (!error) continue;
    if (esDuplicado(error)) {
      const otro = await deQuienEs(c.codigo).catch(() => "");
      problemas.push(
        otro && otro !== String(articulo)
          ? `${c.codigo} ya es del artículo ${otro}. Un código es de un solo artículo: quitalo de ahí primero.`
          : `${c.codigo} ya estaba cargado.`,
      );
    } else {
      problemas.push(`No pude agregar ${c.codigo}: ${error.message}`);
    }
  }
  return problemas;
}
